/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : Users.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { HandleApiActions, userManagementApis } from "../../redux/actions";
import roletypeActions from "../../redux/actions/roletypeActions";
import UpdateUser from "./UpdateUser";
import { convertUtcToTimeZone } from "../../utils/convertUtcToTimeZone";
import deleteIcon from "../../assets/delete_icon.png";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import {
  CustomConfirmationDialog,
  CustomLogsDialog,
  CustomTextFieldN,
  PhoneNumberTextField,
} from "../../components";
import customConsole from "../../config/customConsole";
import appTheme from "../../assets/AppTheme/appTheme";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import CommonBreadcrumbs from "../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import CustomSearchbarWithApiV2 from "../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import CustomDataGridV2 from "../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import CustomFormTextFieldV3 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV3";
import CustomSelectV3 from "../../components/customComponentsV2/common/select/CustomSelectV3";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomFilterTemplate from "../../components/customComponentsV2/common/filter/CustomFilterTemplate";
import SortTemplate from "../../components/customComponentsV2/common/Templates/SortTemplate";
import FilterTemplate from "../../components/customComponentsV2/common/Templates/FilterTemplate";
import { componentStyle } from "../../config/appTheme";

function Users() {
  const dispatch = useDispatch();

  const { usersList } = useSelector(
    (state) => state.userManagementReducer,
    shallowEqual,
  );
  const { countryCodes } = useSelector(
    (state) => state.userManagementReducer,
    shallowEqual,
  );
  const { rolesList } = useSelector(
    (state) => state.userManagementReducer,
    shallowEqual,
  );
  const { rolesListMain } = useSelector(
    (state) => state.userManagementReducer,
    shallowEqual,
  );
  const fetchRoles = () => {
    dispatch(
      HandleApiActions({
        ...userManagementApis.list_role_ddh_ucm,

        params: {
          limit: 100,
          offset: 1,
        },

        show_toast: false,
      }),
    );
  };
  useEffect(() => {
    fetchRoles();
  }, []);

  const profileDetails = useSelector((store) => store.profileReducer);

  const [isRightSideDrawerOpen, setIsRightSideDrawerOpen] = useState(false);
  const [openDrawer, setOpenDrawer] = React.useState(false);
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [drawerData, setDrawerData] = useState(null);
  const [tableRowsData, setTableRowsData] = useState([]);

  const [userData, setUserData] = useState({});
  const [openUserDeleteDialog, setOpenUserDeleteDialog] = useState(false);
  const [userCount, setUserCount] = useState("");

  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(25);

  const [selectedSortColumn] = useState("created_at");
  const [selectedSortOrder] = useState("ASC");

  const [selectedSearchField] = useState("fullname");
  const [searchFieldValue] = useState("");
  const [triggerUserApi, setTriggerUserApi] = useState(false);

  const [openUserChangeLogsDialog, setOpenUserChangeLogsDialog] =
    useState(false);
  const [userChangeLogsList, setUserChangeLogsList] = useState([]);
  const [userLogsDialogData, setUserLogsDialogData] = useState({});
  const [openRejectAccessDialog, setOpenRejectAccessDialog] = useState(false);
  const [reasonToRejectAccess, setReasonToRejectAccess] = useState("");
  const [selectedRejectAccessData, setSelectedRejectAccessData] =
    useState(null);
  const [openForwardApproveAccessDialog, setOpenForwardApproveAccessDialog] =
    useState(false);
  const [selectedRowData] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState("");

  const mapUsersToTableRows = (users = [], currentPage = page) => {
    return users.map((user, index) => ({
      ...user,
      id: (currentPage - 1) * rowsPerPage + index + 1,
      role: user?.role_details?.role_name,
      department: user?.role_details?.department,
      description: user?.role_details?.description,
      region: user?.profile?.region,
      created_at: convertUtcToTimeZone(
        user?.created_at,
        profileDetails?.profile?.profile?.region,
      ),
    }));
  };

  useEffect(() => {
    const mappedUsers = mapUsersToTableRows(usersList, page);
    setTableRowsData(mappedUsers);
  }, [usersList, page]);

  useEffect(() => {
    handleGetUsersList();
  }, [
    page,
    rowsPerPage,
    selectedSortColumn,
    selectedSortOrder,
    selectedSearchField,
    searchFieldValue,
  ]);

  const handleGetUsersList = () => {
    dispatch(
      HandleApiActions({
        ...userManagementApis.get_users_list,
        params: {
          page_number: page,
          page_size: rowsPerPage,
          sort_column: selectedSortColumn,
          sort_order: selectedSortOrder,
          ...(searchFieldValue && { [selectedSearchField]: searchFieldValue }),
        },
        show_toast: false,
      }),
    );
  };

  const toggleDrawer = (newOpen) => () => {
    setIsRightSideDrawerOpen(newOpen);
  };

  const addUserToggleDialog = (newOpen) => () => {
    setIsAddUserDialogOpen(newOpen);
  };

  const handleAddUserDialog = () => {
    addUserToggleDialog(true)();
  };

  const handleTableRowClick = (params) => {
    setUserData(params.row);
    setOpenDrawer(true);
  };

  const handleAddNewUserClick = ({
    fullname,
    email,
    phone_code,
    phone,
    region,
    role_id,
  }) => {
    let currentTime = new Date().toISOString();
    customConsole({
      fullname,
      email,
      phone_code,
      phone,
      current_time: currentTime,
      region,
      role_id,
    });

    dispatch(
      HandleApiActions({
        ...userManagementApis.add_user,
        params: {
          fullname,
          email,
          ...(phone && phone_code && { phone_code }),
          ...(phone && { phone }),
          current_time: currentTime,
          region,
          role_id,
        },
        show_toast: true,
      }),
    ).then(() => {
      handleGetUsersList();
    });

    addUserToggleDialog(false)();
  };

  const handleDeleteUserClick = (params) => {
    setSelectedUserId(params?.row?.user_id);
    setOpenUserDeleteDialog(true);
  };

  const COLUMNS = [
    {
      field: "id",
      headerName: "S.No",
      flex: 0.4,
      minWidth: 60,
      maxWidth: 80,
      filterable: false,
      sortable: false,
    },
    { field: "fullname", headerName: "Name", flex: 1, minWidth: 120 },
    { field: "email", headerName: "Email", flex: 1, minWidth: 120 },

    { field: "role", headerName: "Role", flex: 1, minWidth: 120 },

    { field: "created_at", headerName: "Created At", width: 120 },

    {
      field: "actions",
      headerName: "Actions",
      flex: 0.4,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            size="small"
            sx={{ color: "#000000", p: 0.5 }}
            onClick={(e) => {
              e.stopPropagation();
              handleTableRowClick(params);
            }}
          >
            <BorderColorIcon fontSize="inherit" sx={{ fontSize: 19 }} />
          </IconButton>
          <IconButton
            size="small"
            sx={{ p: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteUserClick(params);
            }}
          >
            <img
              src={deleteIcon}
              alt="Delete user"
              style={{ width: 24, height: 24 }}
            />
          </IconButton>
        </Box>
      ),
    },
  ];

  const handleGetUserChangeLogs = ({
    user_id,
    pageNo = 1,
    pageSize = 25,
  }) => {
    let apiDetails = userManagementApis.get_user_change_logs;

    if (user_id) {
      customConsole({
        ...apiDetails,
        params: {
          user_id,
          page_number: pageNo,
          page_size: pageSize,
        },
        show_toast: false,
      });
      dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            user_id,
            page_number: pageNo,
            page_size: pageSize,
          },
          show_toast: false,
        }),
      ).then((resp) => {
        if (resp?.error) {
          setUserChangeLogsList([]);
        } else {
          setUserChangeLogsList(resp?.data || []);
        }
      });
    }
  };

  const handleUserLogsDialogClose = () => {
    setUserLogsDialogData(null);
    setUserChangeLogsList([]);
    setOpenUserChangeLogsDialog(false);
  };

  const handleConfirmRejectAccess = () => {
    rejectForwardAccess(selectedRejectAccessData);
    setOpenRejectAccessDialog(false);
    setSelectedRejectAccessData(null);
    setReasonToRejectAccess("");
  };

  const handleCancelRejectAccess = () => {
    setOpenRejectAccessDialog(false);
    setReasonToRejectAccess("");
  };

  const rejectForwardAccess = (access_request_id) => {
    let apiDetails = userManagementApis.reject_forward_access;
    dispatch(
      HandleApiActions({
        ...apiDetails,
        params: {
          access_request_id: access_request_id,
          update_reason: reasonToRejectAccess,
        },
        show_toast: true,
      }),
    );
  };

  const handleCloseApproveAccessDialog = () => {
    setOpenForwardApproveAccessDialog(false);
  };

  const breadcrumbItems = [
    { label: "Settings" },
    { label: "Accounts & Access" },
    { label: "User Management" },
  ];
  const handleCancelUserDelete = () => {
    setOpenUserDeleteDialog(false);
  };
  const handleConfirmUserDelete = () => {
    let currentTime = new Date().toISOString();

    dispatch(
      HandleApiActions({
        ...userManagementApis.delete_user,
        params: { current_time: currentTime, user_id: selectedUserId },
        show_toast: true,
      }),
    ).then(() => {
      handleGetUsersList();
    });

    setOpenUserDeleteDialog(false);
    toggleDrawer(false)();
  };

  const [selectedSortObjBeforeClick, setSelectedSortObjBeforeClick] = useState({
    sortBy: "created_at",
    sortOrder: "DESC",
  });
  const [selectedSortObjAfterClick, setSelectedSortObjAfterClick] = useState({
    sortBy: "created_at",
    sortOrder: "DESC",
  });

  const [
    selectedFilterByStatusObjBeforeClick,
    setSelectedFilterByStatusObjBeforeClick,
  ] = useState([]);

  const [
    selectedFilterByStatusObjAfterClick,
    setSelectedFilterByStatusObjAfterClick,
  ] = useState([]);

  return (
    <Box
      style={{
        ...styles.rcMainContainer,
        height: "95%",
        minHeight: 0,
        overflow: "hidden",
        p: 0.5,
      }}
    >
      <Box sx={{ m: 1 }}>
        <CommonBreadcrumbs items={breadcrumbItems} />
      </Box>

      <Box sx={styles.tblFun}>
        <Box sx={{ display: "flex" }}>
          <CustomFilterTemplate
            handleDiscard={() => {
              setSelectedSortObjBeforeClick({
                sortBy: "created_at",
                sortOrder: "DESC",
              });
              setSelectedFilterByStatusObjBeforeClick([]);

            }}
            handleClickApply={() => {
              setSelectedSortObjAfterClick(selectedSortObjBeforeClick);
              setSelectedFilterByStatusObjAfterClick(
                selectedFilterByStatusObjBeforeClick,
              );
            }}
            sections={[
              {
                heading: "Sorting",
                content: (
                  <SortTemplate
                    defaultValue={selectedSortObjBeforeClick}
                    handleOnChange={(sortValue) => {
                      setSelectedSortObjBeforeClick(sortValue);
                    }}
                  />
                ),
              },
              {
                heading: "Filter By Active",
                content: (
                  <Box
                    sx={{
                      minHeight: "290px",
                      maxHeight: "290px",
                      overflowY: "auto",
                      ...componentStyle.scroll,
                    }}
                  >
                    <FilterTemplate
                      defaultValue={selectedFilterByStatusObjBeforeClick}
                      handleOnChange={(sortValue) => {
                        setSelectedFilterByStatusObjBeforeClick(sortValue);
                      }}
                      data={rolesListMain?.roles?.map((data) => ({
                        value: data?.role_id,
                        label: data?.name,
                      }))}
                    />
                  </Box>
                ),
              },
            ]}
          />
          <Box sx={{ ml: 2, mt: 0.3 }}>
            <CustomSearchbarWithApiV2
              apiAction={{ ...userManagementApis?.get_users_list_pa }}
              params={{
                page_number: page,
                page_size: rowsPerPage,
                sort_order: selectedSortObjAfterClick.sortOrder,
                sort_column: selectedSortObjAfterClick.sortBy,
                role_id: selectedFilterByStatusObjAfterClick
                  .map((data) => data.value)
                  .join(","),
              }}
              onSearchQueryChange={(newSearch, oldSearch) => {
                if (oldSearch !== newSearch) {
                  setPage(1);
                }
              }}
              searchKey="fullname"
              triggerApi={triggerUserApi}
              size="small"
              type="text"
              variant="outlined"
              placeholder="Search Users..."
              valueError={""}
              isMultiline={false}
              minRows={1}
              maxRows={3}
              disabled={false}
              txtFldStyle={{
                width: "100%",
              }}
              getDataOnSearch={(data, resp) => {
                setUserCount(resp?.total_count);
                const mappedUsers = mapUsersToTableRows(data, page);
                setTableRowsData(mappedUsers);
              }}
            />
          </Box>
        </Box>
        <Box sx={{ mt: 1, mb: 1 }}>
          <AddButtonV2 handleOnClick={handleAddUserDialog} />
        </Box>
      </Box>
      <Box sx={{ p: 1 }}>
        <Box>
          <Box
            sx={{
              height: "76vh",
              width: "100%",
              display: "flex",
              flexDirection: "column",

              mt: 1,
            }}
          >
            <CustomDataGridV2
              rows={tableRowsData}
              columns={COLUMNS}
              onRowClick={handleTableRowClick}
              page={page}
              isMultiLine={true}
              rowsPerPage={rowsPerPage}
              totalItems={userCount}
              showPagination={Boolean(userCount > 0)}
              handlePageChange={(newPage) => setPage(newPage)}
            />
          </Box>
        </Box>
      </Box>




      <Box></Box>
      <RightSideDrawer
        open={isRightSideDrawerOpen}
        drawerData={drawerData}
        setDrawerData={setDrawerData}
        toggleDrawer={toggleDrawer}
      />
      <AddUserDialog
        open={isAddUserDialogOpen}
        addUserToggleDialog={addUserToggleDialog}
        handleAddNewUserClick={handleAddNewUserClick}
        countryCodes={countryCodes}
        rolesList={rolesList}
      />

      <UpdateDeleteRightSideDrawer
        open={openDrawer}
        setOpenDrawer={setOpenDrawer}
        userData={userData}
        handleGetUsersList={handleGetUsersList}
        setTriggerUserApi={setTriggerUserApi}
        setOpenUserDeleteDialog={setOpenUserDeleteDialog}
        openUserDeleteDialog={openUserDeleteDialog}
      />
      <CustomLogsDialog
        open={openUserChangeLogsDialog}
        handleClose={handleUserLogsDialogClose}
        dialogTitle={"User Change Logs"}
        dialogContent={userChangeLogsList}
        data={userLogsDialogData}
        getLogsData={handleGetUserChangeLogs}
      />

      <CustomConfirmationDialog
        open={openRejectAccessDialog}
        onClose={handleCancelRejectAccess}
        dialogTitle={"Reject Access"}
        disableConfirmBtn={reasonToRejectAccess?.length < 5}
        dialogContent={
          <Stack spacing={1.2}>
            <DialogContentText>
              Are you sure you want to reject access?
            </DialogContentText>
            <Box>
              <CustomTextFieldN
                size={"small"}
                placeholder={"Reason to reject access*"}
                type={"text"}
                value={reasonToRejectAccess}
                setValue={setReasonToRejectAccess}
                setValueError={() => null}
                variant={"standard"}
                txtFldStyle={styles.txtStyle}
              />
            </Box>
          </Stack>
        }
        onConfirm={handleConfirmRejectAccess}
      />
      <Dialog open={openForwardApproveAccessDialog}>
        <DialogTitle>Forward Access For Approval</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={1.2}>
            {selectedRowData?.page_details?.length > 0 && (
              <Accordion style={{ marginBottom: "10px" }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" fontWeight="bold">
                    Pages
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {selectedRowData?.page_details?.map((pageItem) => (
                      <Grid item xs={12} key={pageItem?.page_id || pageItem?.page_name}>
                        <Box display="flex" alignItems="center">
                          {pageItem?.page_name || "-"}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            )}

            {selectedRowData?.feature_details?.length > 0 && (
              <Accordion style={{ marginBottom: "10px" }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" fontWeight="bold">
                    Features
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {Object.entries(
                    selectedRowData?.feature_details.reduce((acc, feature) => {
                      if (!acc[feature?.feature_name])
                        acc[feature?.feature_name] = [];
                      acc[feature?.feature_name].push(feature);
                      return acc;
                    }, {}),
                  ).map(([featureName, featureGroup]) => (
                    <Accordion
                      key={featureName}
                      style={{ marginBottom: "8px", marginLeft: "8px" }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body1" gutterBottom>
                          {featureName}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {featureGroup.map((detail) => {
                            if (!detail || typeof detail !== "object")
                              return null;
                            return (
                              <Grid item xs={12} key={detail?.feature_id || detail?.description}>
                                <Box display="flex" alignItems="center">
                                  {detail?.description || "-"}
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </AccordionDetails>
              </Accordion>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{ ...appTheme.commonBtnStyle }}
            color="error"
            variant="contained"
            onClick={() => handleCloseApproveAccessDialog()}
            startIcon={<CloseIcon />}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <CustomDialogBoxV2
        open={openUserDeleteDialog}
        onClose={handleCancelUserDelete}
        title={"Confirm Delete"}
        successButtonName={"Yes"}
        rejectButtonName={"No"}
        onClickSuccess={handleConfirmUserDelete}
        onClickReject={handleCancelUserDelete}
        onConfirm={handleConfirmUserDelete}
        actionButtonStyle={"continuous"}
      >
        <DialogContentText>
          Are you sure you want to delete this user?
        </DialogContentText>
      </CustomDialogBoxV2>
    </Box>
  );
}

export default Users;

function RightSideDrawer({ open, drawerData, setDrawerData, toggleDrawer }) {
  const handleOnClose = () => {
    setDrawerData(null);

    toggleDrawer(false)();
  };

  const DrawerList = (
    <Box sx={{ width: 270 }} onClick={toggleDrawer(false)}>
      <Typography mt={1} textAlign={"center"} variant="h5" gutterBottom>
        {drawerData?.heading}
      </Typography>
      <Divider />
      <List>
        {drawerData?.items?.map((text) => (
          <ListItem key={text}>
            <ListItemText primary={text} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <div>
      <Drawer anchor="right" open={open} onClose={handleOnClose}>
        {DrawerList}
      </Drawer>
    </div>
  );
}

RightSideDrawer.propTypes = {
  open: PropTypes.bool,
  drawerData: PropTypes.shape({
    heading: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.string),
  }),
  setDrawerData: PropTypes.func,
  toggleDrawer: PropTypes.func,
};

const mapRoleOptions = (roles = []) => {
  return roles
    ?.filter((role) => role?.is_active)
    ?.map((role) => ({
      value: role?.role_id,
      label: role?.name,
    }))
    ?.sort((a, b) => (a?.label || "").localeCompare(b?.label || ""));
};

const AddUserDialog = ({
  open,
  addUserToggleDialog,
  handleAddNewUserClick,
  countryCodes,
}) => {
  const [fullName, setFullName] = useState("");
  const [fullNameError, setFullNameError] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [regionOptions, setRegionOptions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedRoleDepartmentId, setSelectedRoleDepartmentId] =
    useState(null);
  const [roleDepartmentOptions, setRoleDepartmentOptions] = useState([]);
  const [phoneCodeOptions, setPhoneCodeOptions] = useState([]);
  const [selectedPhoneCode, setSelectedPhoneCode] = useState("");
  const [selectedRoleType, setSelectedRoleType] = useState("");
  const [selectedRoleTypeName, setSelectedRoleTypeName] = useState("");
  const dispatch = useDispatch();

  const clearDataFormFields = () => {
    setFullName("");
    setFullNameError("");
    setEmail("");
    setEmailError("");
    setSelectedRoleDepartmentId(null);
    setSelectedRegion("");
    setSelectedPhoneCode("");
    setPhoneNumber("");
    setPhoneNumberError("");
    setSelectedRoleType("");
    setSelectedRoleTypeName("");
    setRoleDepartmentOptions([]);
  };

  const handleClose = () => {
    addUserToggleDialog(false)();

    clearDataFormFields();
  };

  useEffect(() => {
    let filteredRegions = countryCodes
      ?.map((el) => {
        return { value: el.time_zone, label: el.time_zone };
      })
      .sort((a, b) => {
        return a.value.localeCompare(b.value);
      });

    let filteredPhoneCodes = countryCodes
      ?.map((el) => {
        return {
          value: el.phone_code,
          label: `${el.country}: ${el.phone_code}`,
          phone_code: el.phone_code,
        };
      })
      .sort((a, b) => {
        return a.label.localeCompare(b.label);
      });

    setRegionOptions(filteredRegions);
    setPhoneCodeOptions(filteredPhoneCodes);
  }, [countryCodes]);

  const roleTypesAll = useSelector(
    (state) => state.roleTypeReducers.roleTypesAll,
  );

  useEffect(() => {
    dispatch(
      HandleApiActions({
        ...roletypeActions.get_all_roletype,
        params: {
          isActive: true,
          isSystem: false,
          page: 0,
          page_size: 99,
        },
        show_toast: false,
      }),
    );
  }, [dispatch]);

  const roleTypeOptions = roleTypesAll?.data?.roleTypes
    ?.map((roleType) => ({
      value: roleType?.role_type_id,
      label: roleType?.display_name,
      name: roleType?.display_name,
    }))
    ?.sort((a, b) => (a?.label || "").localeCompare(b?.label || ""));

  useEffect(() => {
    const fetchRolesByRoleTypeName = () => {
      if (!selectedRoleTypeName) {
        setRoleDepartmentOptions([]);
        return;
      }

      dispatch(
        HandleApiActions({
          ...userManagementApis.list_role_ddh_ucm,
          params: {
            roleTypeId: selectedRoleTypeName,
            is_active: true,
            is_system: false,
          },
          show_toast: false,
        }),
      ).then((resp) => {
        const roles = resp?.data?.roles || [];
        setRoleDepartmentOptions(mapRoleOptions(roles));
      });
    };

    fetchRolesByRoleTypeName();
  }, [dispatch, selectedRoleTypeName]);

  const isFormValid = () => {
    return (
      fullName?.trim() !== "" &&
      !fullNameError &&
      email?.trim() !== "" &&
      !emailError &&
      (phoneNumber?.trim() === "" || phoneNumber?.length === 10) &&
      selectedRoleDepartmentId &&
      selectedRegion !== ""
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          position: "relative",
          textAlign: "center",
          borderBottom: "1px solid #e0e0e0",
          pb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Add Users
        </Typography>

        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <CloseIcon />
        </IconButton>

      </DialogTitle>

      <DialogContent
        sx={{
          mt: 2,
          maxHeight: "60vh",
          overflowY: "auto",
          pr: 1,
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#b0b0b0",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#888",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f1f1f1",
            borderRadius: "8px",
          },
        }}
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 1,
              width: "100%",
            }}
          >
            <CustomFormTextFieldV2
              size="small"
              label="Full Name"
              placeholder="Full Name"
              type="text"
              required={true}
              fldType="text"
              value={fullName}
              setValue={setFullName}
              valueError={fullNameError}
              setValueError={setFullNameError}
              maxLength={80}
            />

            <CustomFormTextFieldV3
              size="small"
              label="Email"
              placeholder="Email"
              required={true}
              type="email"
              fldType="email"
              value={email}
              setValue={setEmail}
              valueError={emailError}
              setValueError={setEmailError}
            />

            <CustomSelectV3
              size="small"
              label="Role Type"
              placeholder={"Select Role Type"}
              required={true}
              optionsList={roleTypeOptions}
              selectedValue={selectedRoleType}
              handleOnChange={(e) => {
                const newValue = e.target.value;
                setSelectedRoleType(newValue);
                setSelectedRoleDepartmentId(null);
                const selectedOption = roleTypeOptions?.find(
                  (option) => String(option?.value) === String(newValue),
                );
                setSelectedRoleTypeName(selectedOption?.value || "");
              }}
            />

            <CustomSelectV3
              size="small"
              label="Role"
              placeholder={"Select Role"}
              required={true}
              optionsList={roleDepartmentOptions}
              selectedValue={selectedRoleDepartmentId}
              handleOnChange={(e) =>
                setSelectedRoleDepartmentId(e.target.value)
              }
            />

            <CustomSelectV3
              size="small"
              label="Time Zone"
              placeholder={"Select Timezone"}
              optionsList={regionOptions}
              selectedValue={selectedRegion}
              handleOnChange={(e) => setSelectedRegion(e.target.value)}
              required={true}
            />

            <PhoneNumberTextField
              size="small"
              variant="outlined"
              phoneCodeLabel="Country"
              phoneNumberLabel="Phone Number (Optional)"
              phoneCodeOptions={phoneCodeOptions}
              selectedPhoneCode={selectedPhoneCode}
              setSelectedPhoneCode={setSelectedPhoneCode}
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              phoneNumberError={phoneNumberError}
              setPhoneNumberError={setPhoneNumberError}
              phoneCodeStyle={{
                width: "340px",
              }}
              phoneNumberStyle={{
                width: "512px",
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Box sx={{ display: "flex" }}>
          <CustomButtonV2
            type="cancel"
            btnName="Cancel"
            handleOnClick={handleClose}
          />

          <CustomButtonV2
            type="submit"
            btnName="Add"
            disabled={!isFormValid()}
            handleOnClick={() => {
              handleAddNewUserClick({
                fullname: fullName,
                email,
                phone_code: selectedPhoneCode,
                phone: phoneNumber,
                region: selectedRegion,
                role_id: Number(selectedRoleDepartmentId),
              });

              clearDataFormFields();
            }}
            btnStyle={{ ml: 1 }}
          />
        </Box>
      </DialogActions>
    </Dialog>
  );
};

AddUserDialog.propTypes = {
  open: PropTypes.bool,
  addUserToggleDialog: PropTypes.func,
  handleAddNewUserClick: PropTypes.func,
  countryCodes: PropTypes.arrayOf(
    PropTypes.shape({
      time_zone: PropTypes.string,
      phone_code: PropTypes.string,
      country: PropTypes.string,
    }),
  ),
};

function UpdateDeleteRightSideDrawer({
  open,
  userData,
  setOpenDrawer,
  setTriggerUserApi,
  setOpenUserDeleteDialog,
  openUserDeleteDialog,
}) {
  const handleOnClose = () => {
    setOpenDrawer(false);
  };

  return (
    <Dialog open={open} onClose={handleOnClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          position: "relative",
          textAlign: "center",
          borderBottom: "1px solid #e0e0e0",
          pb: 1,
        }}
      >
        <Typography sx={{ fontWeight: 600 }}>Update Users</Typography>

        <IconButton
          onClick={handleOnClose}
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>


      <UpdateUser
        setOpenUserDeleteDialog={setOpenUserDeleteDialog}
        openUserDeleteDialog={openUserDeleteDialog}
        userData={userData}
        setOpenDrawer={setOpenDrawer}
        handleGetUsersList={() => {
          setTriggerUserApi((prev) => !prev);
        }}
      />
    </Dialog>
  );
}

UpdateDeleteRightSideDrawer.propTypes = {
  open: PropTypes.bool,
  userData: PropTypes.object,
  setOpenDrawer: PropTypes.func,
  setTriggerUserApi: PropTypes.func,
  setOpenUserDeleteDialog: PropTypes.func,
  openUserDeleteDialog: PropTypes.bool,
};

const styles = {
  tblFun: {
    display: "flex",
    justifyContent: "space-between",
    ml: 1,
    mr: 1,
    mt: 1,
  },
  mainContainer: {
    marginBottom: "60px",
  },
  container: {
    width: "100%",
    overflow: "hidden",
  },
  btnsContainer: {
    display: "flex",
    gap: 1,
  },
  userPaper: {
    height: 370,
    width: "100%",
    mb: 14,
    scrollbarColor: "#888 #f1f1f1",
  },
  paginationPaper: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "10px",
  },
  customButton: {
    ml: 10,
  },
  customTextFieldN: {
    width: "380px",
  },
};
